/**
 * Definition Policy Store
 *
 * Workspace-level store for field readiness behavior overrides.
 * The source of truth is the workspace_definition_policy table.
 * This store is a workspace-scoped read cache — it does NOT use localStorage.
 *
 * Usage:
 *   const getBehavior = useDefinitionPolicyStore(s => s.getBehavior);
 *   getBehavior('itemCategory') // => 'block' | 'warn' | 'optional'
 */

import { create } from 'zustand';
import {
  fetchDefinitionPolicy,
  setFieldBehavior,
  resetFieldBehavior,
} from '../../lib/definitionPolicyService';
import {
  STRUCTURAL_FIELD_METAS,
  type DefinitionBehavior,
  type DefinitionPolicy,
} from '../../types/prioritization/definitionPolicy';
import type { Attribute } from '../../types/prioritization';

interface DefinitionPolicyState {
  /** Current workspace loaded */
  workspaceId: string | null;
  /** Overrides from DB — only explicitly set keys are present */
  overrides: DefinitionPolicy;
  loading: boolean;
  error: string | null;

  /**
   * Load policy overrides for a workspace.
   * Safe to call multiple times — no-ops if the workspace hasn't changed.
   */
  loadForWorkspace: (workspaceId: string) => Promise<void>;

  /**
   * Returns the effective behavior for a field key.
   * Falls back to:
   *   1. DB override (loaded via loadForWorkspace)
   *   2. STRUCTURAL_FIELD_METAS default
   *   3. For custom attributes: 'warn' if required flag is true, else 'optional'
   */
  getBehavior: (
    fieldKey: string,
    attributeFallback?: Pick<Attribute, 'required'>
  ) => DefinitionBehavior;

  /**
   * Persist a behavior override for a single field.
   * Optimistically updates local state, then writes to DB.
   */
  setFieldBehavior: (
    fieldKey: string,
    behavior: DefinitionBehavior,
    userId?: string
  ) => Promise<{ error: string | null }>;

  /**
   * Remove a field override, reverting to code default.
   */
  resetField: (fieldKey: string) => Promise<{ error: string | null }>;

  /** Clear all state (called on workspace switch or logout). */
  clear: () => void;
}

export const useDefinitionPolicyStore = create<DefinitionPolicyState>()((set, get) => ({
  workspaceId: null,
  overrides: {},
  loading: false,
  error: null,

  loadForWorkspace: async (workspaceId) => {
    if (get().workspaceId === workspaceId && !get().loading) {
      // Already loaded — skip unless it's a fresh mount
      if (Object.keys(get().overrides).length > 0 || get().error !== null) return;
    }
    set({ loading: true, error: null, workspaceId });
    const { data, error } = await fetchDefinitionPolicy(workspaceId);
    set({ loading: false, overrides: data, error });
  },

  getBehavior: (fieldKey, attributeFallback) => {
    if (fieldKey === 'title') return 'block';

    const { overrides } = get();
    if (fieldKey in overrides) return overrides[fieldKey]!;

    // Structural field default
    const meta = STRUCTURAL_FIELD_METAS.find((m) => m.fieldKey === fieldKey);
    if (meta) return meta.defaultBehavior;

    // Custom attribute default: an attribute marked required warns; nothing but
    // the title is a Hard Stop until an admin makes it one (owner ruling
    // 2026-10-15, "warning by default"). Values usually arrive after an item
    // joins a pool, so a Hard Stop here would keep items out of every pool.
    if (attributeFallback) {
      return attributeFallback.required ? 'warn' : 'optional';
    }

    return 'optional';
  },

  setFieldBehavior: async (fieldKey, behavior, userId) => {
    if (fieldKey === 'title') return { error: null };

    const workspaceId = get().workspaceId;
    if (!workspaceId) return { error: 'No workspace loaded' };

    // Optimistic update
    set((state) => ({
      overrides: { ...state.overrides, [fieldKey]: behavior },
    }));

    const result = await setFieldBehavior(workspaceId, fieldKey, behavior, userId);

    if (result.error) {
      // Rollback
      const { data } = await fetchDefinitionPolicy(workspaceId);
      set({ overrides: data });
    }

    return result;
  },

  resetField: async (fieldKey) => {
    const workspaceId = get().workspaceId;
    if (!workspaceId) return { error: 'No workspace loaded' };

    // Optimistic: remove override so code default applies
    set((state) => {
      const next = { ...state.overrides };
      delete next[fieldKey];
      return { overrides: next };
    });

    return resetFieldBehavior(workspaceId, fieldKey);
  },

  clear: () => set({ workspaceId: null, overrides: {}, loading: false, error: null }),
}));
